import { Button, Input, Select, Spin, TableProps, Tooltip } from "antd";
import { capitalize } from "lodash";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import EditIcon from "../../../../assets/icons/EditIcon";
import PayrollImportOutlined from "../../../../assets/icons/PayrollImportOutlined";
import RejectIcon from "../../../../assets/icons/RejectIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import ConfirmActionModal from "../../../../components/Businesses/ConfirmActionModal";
import SuccessModal from "../../../../components/common/SuccessModal";
import WalletBalanceChecker from "../../../../components/common/WalletBalanceChecker";
import CustomTable from "../../../../components/ui/CustomTable";
import Loader from "../../../../components/ui/Loader";
import {
  useBatchRejectByMakerMutation,
  useGetAllBatchQuery,
  useGetAllDisbursementTypeQuery,
} from "../../../../features/portal/batch/batchApi";
import {
  useGetSingleCompanyQuery,
  useGetSingleUserWalletRoleQuery,
  useGetUserWalletRoleByUserIdQuery,
} from "../../../../features/portal/companyOnboard/companyOnboardApi";
import { useGetProjectsByUserQuery } from "../../../../features/portal/Products/productsApi";
import { useBalanceCheckQuery } from "../../../../features/portal/vendorPayment/vendorPaymentApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import { formatDate, formatTime } from "../../../../lib/time";
import { toast } from "../../../../lib/toast";
import {
  GetAllBatchDataParamsType,
  GetAllBatchDataType,
} from "../../../../types/portal/batch";
import { countParPageSerialNumber } from "../../../../utils/number";
import { addKeyInArray } from "../../../../utils/utils";
import AddWalletModal from "../../ShebaPayWallet/AddWalletModal";
import ImportDataDigigoModal from "../Payments/ImportDataDigigoModal";
import PaymentSalaryDisModal from "../Payments/PaymentSalaryDisModal";
import BatchEditModal from "./BatchEditModal";

type Props = {
  walletRole?: string;
  walletRoleData?: any;
};

export default function BatchV2({ walletRole, walletRoleData }: Props) {
  const [visibleRows, setVisibleRows] = useState(1);
  const { walletId } = useParams();
  const navigate = useNavigate();

  const {
    page,
    limit,
    handlePagination,
    handleFilter,
    handleLimit,
    filter,
    keyword,
    handleSearch,
  } = useTable<GetAllBatchDataParamsType>();

  const {
    open: addWalletOpen,
    handleOpen: addWalletHandleOpen,
    handleClose: addWalletHandleClose,
  } = useModal<number | undefined, "ADD" | "UPDATE">();

  const { open, handleOpen, handleClose } = useModal<
    undefined,
    "CONFIRM_FOR_APPROVAL" | "IMPORT_DIGIGO" | "EDIT"
  >(reRender);
  const {
    open: openConfirmRejectModalApprover,
    handleOpen: handleConfirmRejectApproverOpen,
    handleClose: handleConfirmRejectApproverClose,
  } = useModal<any, "CONFIRM_REJECT_APPROVER">();
  const {
    open: openSuccessModal,
    handleOpen: handleSuccessOpen,
    handleClose: handleSuccessClose,
  } = useModal<undefined, "SUCCESS">();
  const roleCheck = localStorage.getItem("project_role");
  const projectId = localStorage.getItem("product_id");
  const { user, role } = useAppSelector((state) => state.auth);
  const {
    isError: isProjectError,
    error: projectError,
    isLoading: isProjectLoading,
    data: projectData,
  } = useGetProjectsByUserQuery({
    params: {
      userid: user?.data?.id,
    },
  });

  const {
    data: getSingleUserWalletRoleData,
    isError: getSingleUserWalletRoleIsError,
    error: getSingleUserWalletRoleError,
    isLoading: getSingleUserWalletRoleIsLoading,
  } = useGetSingleUserWalletRoleQuery(
    {
      walletId: walletId ? Number(walletId) : undefined,
      userId: user?.data?.id,
    },
    { skip: !walletId || !user?.data?.id }
  );

  const { isError, error, isLoading, data, refetch } = useGetAllBatchQuery(
    {
      params: {
        ...filter,
        skip: undefined,
        search: keyword,
        company_id: user?.data?.company_id,
        projectId: Number(projectId),
        page,
      },
    },
    {
      skip: !user?.data?.company_id || !projectId,
    }
  );

  const {
    isLoading: isCompanyDetailsLoading,
    isError: isCompanyDetailsError,
    error: companyDetailsError,
    data: companyDetails,
  } = useGetSingleCompanyQuery(user?.data?.company_id, {
    skip: !user?.data?.company_id,
  });
  const {
    isLoading: isBalanceCheckLoading,
    isError: isBalanceCheckError,
    error: balanceCheckError,
    data: balanceCheck,
  } = useBalanceCheckQuery(
    {
      walletNumber:
        getSingleUserWalletRoleData?.data?.wallet?.wallet_number || "",
      companyId: user?.data?.company_id,
    },
    {
      skip:
        !getSingleUserWalletRoleData?.data?.wallet?.wallet_number ||
        !user?.data?.company_id,
    }
  );
  const [
    batchRejectByMaker,
    {
      isLoading: batchRejectByMakerIsLoading,
      isSuccess: batchRejectByMakerIsSuccess,
      isError: batchRejectByMakerIsError,
      error: batchRejectByMakerError,
    },
  ] = useBatchRejectByMakerMutation();

  const {
    data: getAllDisbursementTypeData,
    isError: getAllDisbursementTypeIsError,
    error: getAllDisbursementTypeError,
  } = useGetAllDisbursementTypeQuery({
    params: {
      is_active: true,
    },
  });

  const {
    data: userWalletsData,
    isError: userWalletsIsError,
    error: userWalletsError,
    isLoading: userWalletsIsLoading,
  } = useGetUserWalletRoleByUserIdQuery(
    {
      id: Number(user?.data?.id),
      search: keyword,
      page,
      limit,
    },
    {
      skip: !user?.data?.id,
    }
  );

  useError(
    userWalletsIsError,
    userWalletsError,
    "Get User Wallet Role By User Id failed"
  );

  useError(
    getSingleUserWalletRoleIsError,
    getSingleUserWalletRoleError,
    "Get single user wallet role failed"
  );
  useError(isError, error, "Get all batch fail");
  useError(isProjectError, projectError, "Get project fail");
  useError(isCompanyDetailsError, companyDetailsError, "Get company data fail");
  useError(isBalanceCheckError, balanceCheckError, "Get balance data fail");
  useError(
    batchRejectByMakerIsError,
    batchRejectByMakerError,
    "Batch reject fail"
  );
  useError(
    getAllDisbursementTypeIsError,
    getAllDisbursementTypeError,
    "Get disbursement type fail"
  );

  const digigoData = {
    company_id: user?.data?.company_id,
    created_by: user?.data?.id,
    project_id: projectId ? Number(projectId) : undefined,
  };

  function reRender() {
    refetch();
  }

  const columns: TableProps<GetAllBatchDataType>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: <div className="whitespace-nowrap !w-[120px]">Batch Name</div>,
      dataIndex: "batchName",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <div className="whitespace-nowrap !w-[120px]">Batch Code</div>,
      dataIndex: "batchNo",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <div className="whitespace-nowrap !w-[120px]">Wallet Number</div>,
      dataIndex: "wallet_number",
      align: "center",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Import Date</span>,
      dataIndex: "created_at",
      render: (val: string) =>
        val ? (
          <>
            {formatDate(val)}, {formatTime(val)}
          </>
        ) : (
          "N/A"
        ),
    },
    {
      title: <span className="whitespace-nowrap">Number of Employees</span>,
      align: "center",
      dataIndex: "numberOfEmployees",
      render: (val) => (val ? val : 0),
    },
    {
      title: <span className="whitespace-nowrap">Amount (BDT)</span>,
      align: "center",
      dataIndex: "totalAmount",
      render: (val: string | number) => (
        <>
          ৳{" "}
          {val
            ? Number(val)
                .toFixed(2)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            : "0"}
        </>
      ),
    },
    {
      title: <span className="whitespace-nowrap">Disbursed Amount (BDT)</span>,
      align: "center",
      dataIndex: "totalDisbursed",
      render: (val: string | number) => (
        <>
          ৳{" "}
          {val
            ? Number(val)
                .toFixed(2)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            : "0"}
        </>
      ),
    },
    {
      title: <span className="whitespace-nowrap">Pending Amount (BDT)</span>,
      align: "center",
      dataIndex: "totalPending",
      render: (val: string | number) => (
        <>
          ৳{" "}
          {val
            ? Number(val)
                .toFixed(2)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            : "0"}
        </>
      ),
    },
    {
      title: <span className="whitespace-nowrap">Disbursement Type</span>,
      align: "center",
      dataIndex: "dis_type",
      render: (val: string) =>
        val ? val.charAt(0).toUpperCase() + val.slice(1).toLowerCase() : "N/A",
    },
    {
      title: <span className="whitespace-nowrap">Payment Type</span>,
      align: "center",
      dataIndex: "disbursement_type",
      render: (val: string) =>
        val ? val.charAt(0).toUpperCase() + val.slice(1).toLowerCase() : "N/A",
    },
    {
      title: <span className="whitespace-nowrap">Created At</span>,
      align: "center",
      dataIndex: "created_at",
      render: (val: string) =>
        val ? (
          <>
            {formatDate(val)}, {formatTime(val)}
          </>
        ) : (
          "N/A"
        ),
    },
    {
      title: <span className="whitespace-nowrap">Updated At</span>,
      align: "center",
      dataIndex: "updated_at",
      render: (val: string) =>
        val ? (
          <>
            {formatDate(val)}, {formatTime(val)}
          </>
        ) : (
          "N/A"
        ),
    },
    {
      title: <div className="whitespace-nowrap !w-[110px]">Status</div>,
      dataIndex: "batchStatus",
      align: "center",
      render: (
        val: "pending" | "completed" | "failed" | "rejected" | string
      ) => {
        const statusClasses: Record<string, string> = {
          pending: "bg-yellow-100 text-yellow-700",
          PartiallySettled: "bg-yellow-100 text-yellow-700",
          completed: "bg-green-100 text-green-700",
          failed: "bg-red-100 text-red-700",
          rejected: "bg-red-100 text-red-700",
        };

        return (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${
              statusClasses[val] || "bg-yellow-100 text-yellow-700"
            }`}
          >
            {val === "partially_settled"
              ? "Partially Settled"
              : capitalize(val)}
          </span>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "id",
      align: "center",
      render: (val, record: any) => (
        <div className="flex justify-center items-center gap-2">
          <Tooltip title="Batch Edit">
            <Button
              className="action-button disabled:!bg-[#ddd]"
              size="small"
              icon={<EditIcon />}
              onClick={() => {
                if (
                  record?.status === "maker" &&
                  record?.batchStatus === "pending"
                ) {
                  handleOpen({ type: "EDIT", data: record });
                } else {
                  toast("warning", "Batch can not be edited");
                }
              }}
            />
          </Tooltip>

          <Tooltip title="Batch Reject">
            <Button
              className="action-button disabled:!bg-[#ddd]"
              size="small"
              icon={<RejectIcon />}
              onClick={() => {
                if (record?.status === "maker") {
                  handleConfirmRejectApproverOpen({
                    type: "CONFIRM_REJECT_APPROVER",
                    data: record,
                  });
                } else {
                  toast("warning", "Batch can not be rejected");
                }
              }}
            />
          </Tooltip>

          <Tooltip title="Batch Open">
            <Link
              to={`/admin/salary-disbursement/batch/payments/${val}/${record?.wallet_number}`}
            >
              <Button
                className="action-button disabled:!bg-[#ddd]"
                size="small"
                icon={<ViewIcon />}
              />
            </Link>
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleOkay = () => {
    handleSuccessClose();
    handleConfirmRejectApproverClose();
  };

  const handleBatchRejectApprover = (value: string) => {
    if (openConfirmRejectModalApprover?.data?.batchStatus === "rejected") {
      toast("warning", "Batch already rejected");
      return;
    }

    const newData = {
      remarks: value,
    };

    batchRejectByMaker({
      data: newData,
      id: openConfirmRejectModalApprover?.data?.id,
    });
  };

  const cardColumns = 3; // maximum columns in grid (lg screens)
  const visibleItems = visibleRows * cardColumns; // number of items to show

  const hasMoreItems = visibleItems < walletRoleData?.length;

  const handleToggle = () => {
    if (hasMoreItems) {
      // Show 1 more row
      setVisibleRows(visibleRows + 1);
    } else {
      // Collapse back to 1 row
      setVisibleRows(1);
    }
  };

  useEffect(() => {
    if (batchRejectByMakerIsSuccess) {
      handleSuccessOpen({ type: "SUCCESS" });
      reRender();

      // toastMessage.success("Payment data sent successfully");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchRejectByMakerIsSuccess]);

  useEffect(() => {
    if (getSingleUserWalletRoleIsError) {
      setTimeout(() => {
        navigate("/admin/salary-disbursement/batch");
      }, 1500);
    }
  }, [getSingleUserWalletRoleIsError, navigate]);

  console.log("walletRoleDta:", walletRoleData);

  return (
    <>
      {open?.type === "IMPORT" && (
        <PaymentSalaryDisModal
          open={open.open}
          handleClose={handleClose}
          walletId={walletId}
        />
      )}
      {open?.type === "IMPORT_DIGIGO" && (
        <ImportDataDigigoModal
          open={open.open}
          handleClose={handleClose}
          data={digigoData}
          reRender={reRender}
        />
      )}

      {open?.type === "EDIT" && (
        <BatchEditModal
          open={open?.open}
          handleClose={handleClose}
          data={open?.data}
        />
      )}

      {addWalletOpen?.type === "ADD" || addWalletOpen?.type === "UPDATE" ? (
        <AddWalletModal
          open={addWalletOpen?.open}
          handleClose={addWalletHandleClose}
          data={addWalletOpen?.data}
          type={addWalletOpen?.type}
          isApprover={walletRole}
        />
      ) : null}

      {openConfirmRejectModalApprover.type === "CONFIRM_REJECT_APPROVER" && (
        <ConfirmActionModal
          title="Are you sure you want to reject this batch?"
          handleClose={handleConfirmRejectApproverClose}
          open={openConfirmRejectModalApprover.open}
          handleBatchRejectSubmit={(val) =>
            handleBatchRejectApprover(String(val))
          }
          isLoading={batchRejectByMakerIsLoading}
          isReject
        />
      )}

      {openSuccessModal.type === "SUCCESS" && (
        <SuccessModal
          open={openSuccessModal?.open}
          handleClose={handleSuccessClose}
          handleOkay={handleOkay}
          data={openSuccessModal?.data}
          title={"Batch Rejected Successfully!"}
        />
      )}

      <div>
        {isLoading ||
        getSingleUserWalletRoleIsLoading ||
        userWalletsIsLoading ? (
          <Loader />
        ) : (
          <>
            {/* <Card className="mb-4">
              <p className="text-blue-500 text-2xl font-medium leading-tight flex items-center gap-3">
                <div>Role: {capitalize(walletRole)}</div>
              </p>
            </Card>  */}

            <div className="mb-5">
              <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {walletRoleData?.slice(0, visibleItems)?.map((item: any) => (
                  <div
                    className="!w-full !px-2 !py-6 border border-[#D7DFE9] rounded-[7px] bg-white"
                    key={item?.id}
                  >
                    {isCompanyDetailsLoading ? (
                      <Loader />
                    ) : user?.data?.company_id &&
                      (roleCheck === "Admin" ||
                        (walletRole === "approver" && roleCheck === "User") ||
                        (walletRole === "maker" && roleCheck === "User") ||
                        (walletRole === "checker" && roleCheck === "User") ||
                        role === "SUPER_ADMIN") ? (
                      <>
                        <div className="flex justify-between items-center">
                          <div className="flex justify-start items-center gap-2">
                            {/* <img
                                  src={
                                    companyDetails?.data?.logo || AvatarImage
                                  }
                                  alt=""
                                  className="w-14 h-14 rounded-full"
                                /> */}
                            <div className="">
                              <div className="text-blue-500 text-sm font-semibold mb-0.5">
                                {item?.wallet?.wallet_name}
                              </div>
                              {item?.wallet?.wallet_number ? (
                                <p className="text-blue-500 text-sm font-normal flex items-center gap-3">
                                  <span>
                                    {item?.wallet?.wallet_number || "N/A"}{" "}
                                  </span>
                                  {roleCheck === "Admin" ||
                                  (walletRole === "approver" &&
                                    roleCheck === "User") ? (
                                    <span
                                      className="cursor-pointer"
                                      onClick={() =>
                                        addWalletHandleOpen({
                                          type: "UPDATE",
                                          data: Number(item?.wallet?.id),
                                        })
                                      }
                                    >
                                      <EditIcon />
                                    </span>
                                  ) : null}
                                </p>
                              ) : // ) : roleCheck === "Admin" ||
                              //   roleCheck === "Approver" ||
                              //   role === "SUPER_ADMIN" ? (
                              //   <Button
                              //     icon={<AddIcon />}
                              //     onClick={() =>
                              //       addWalletHandleOpen({ type: "ADD" })
                              //     }
                              //     className="mt-2"
                              //     type="primary"
                              //     size="small"
                              //   >
                              //     Add Wallet
                              //   </Button>
                              null}
                            </div>
                          </div>

                          {isBalanceCheckLoading ? (
                            <Spin />
                          ) : user?.data?.company_id ? (
                            // <BalanceSlide>
                            //   {addCommaInNumber(
                            //     Math.round(
                            //       balanceCheck?.data?.data?.Amount || 0
                            //     )
                            //   )}
                            //   BDT
                            //       </BalanceSlide>

                            <WalletBalanceChecker
                              wallet_number={item?.wallet?.wallet_number}
                              company_id={user?.data?.company_id}
                            />
                          ) : null}
                        </div>
                      </>
                    ) : null}

                    {/* {roleCheck === "Checker" || roleCheck === "Maker" ? (
                      <div className="mb-3  flex justify-end">
                        {isBalanceCheckLoading ? (
                          <Spin />
                        ) : user?.data?.company_id ? (
                          <BalanceSlide>
                            {addCommaInNumber(
                              Math.round(balanceCheck?.data?.data?.Amount || 0)
                            )}
                            BDT
                          </BalanceSlide>
                        ) : null}
                      </div>
                    ) : null} */}
                  </div>
                ))}
              </div>

              {walletRoleData?.length > 3 && (
                <div className="mt-3 flex justify-end">
                  <Button
                    type="default"
                    className="!text-[#FE7235] border border-[#FE7235]"
                    onClick={handleToggle}
                  >
                    {hasMoreItems ? "View More" : "View Less"}
                  </Button>
                </div>
              )}
            </div>

            <CustomTable
              loading={isLoading || getSingleUserWalletRoleIsLoading}
              limit={limit}
              page={page}
              columns={columns}
              total={data?.data?.total}
              dataSource={addKeyInArray(data?.data?.data)}
              handleLimit={handleLimit}
              handlePagination={handlePagination}
              filterSection={
                <>
                  <Input
                    suffix={<SearchIcon />}
                    placeholder="Batch Name"
                    style={{ width: 205 }}
                    allowClear
                    onChange={handleSearch}
                  />

                  <Select
                    placeholder="Wallet Name"
                    showSearch
                    options={userWalletsData?.data?.walletRoles?.map(
                      (item: any) => ({
                        label: item?.wallet?.wallet_name,
                        value: item?.wallet?.wallet_number,
                      })
                    )}
                    value={filter?.wallet_number ?? undefined}
                    onChange={(value) => handleFilter("wallet_number", value)}
                    className="!min-w-[150px]"
                    suffixIcon={<SelectDownIcon />}
                    // style={{ width: "100%" }}
                    allowClear
                    optionFilterProp="label"
                  />

                  <Select
                    placeholder="Disbursement Type"
                    showSearch
                    options={getAllDisbursementTypeData?.data?.map(
                      (item: any) => ({
                        label: item.name,
                        value: item.name,
                      })
                    )}
                    value={filter?.dis_type ?? undefined}
                    onChange={(value) => handleFilter("dis_type", value)}
                    className="!min-w-[110px]"
                    suffixIcon={<SelectDownIcon />}
                    // style={{ width: "100%" }}
                    allowClear
                    optionFilterProp="label"
                  />

                  <Select
                    placeholder="Payment Type"
                    showSearch
                    options={[
                      { label: "Both", value: "both" },
                      { label: "Bank", value: "bank" },
                      { label: "Wallet", value: "wallet" },
                    ]}
                    value={filter?.disbursement_type ?? undefined}
                    onChange={(value) =>
                      handleFilter("disbursement_type", value)
                    }
                    className="!min-w-[110px]"
                    suffixIcon={<SelectDownIcon />}
                    // style={{ width: "100%" }}
                    allowClear
                    optionFilterProp="label"
                  />
                  <Select
                    placeholder="Status"
                    showSearch
                    options={[
                      { label: "Pending", value: "pending" },
                      {
                        label: "Partially Settled",
                        value: "partially_settled",
                      },
                      { label: "Completed", value: "completed" },
                      { label: "Failed", value: "failed" },
                      { label: "Rejected", value: "rejected" },
                    ]}
                    value={filter?.batch_status ?? undefined}
                    onChange={(value) => handleFilter("batch_status", value)}
                    className="!min-w-[110px]"
                    suffixIcon={<SelectDownIcon />}
                    // style={{ width: "100%" }}
                    allowClear
                    optionFilterProp="label"
                  />

                  {walletRole === "maker" && roleCheck === "User" && (
                    <>
                      {!!projectData?.data?.find(
                        (item) => item?.project?.id === 4
                      ) && (
                        <Link
                          to={`/admin/salary-disbursement/batch/pending-list/${
                            projectData?.data?.find(
                              (item) => item?.project?.id === 4
                            )?.project?.id
                          }`}
                        >
                          <Button
                            // onClick={() => handleOpen({ type: "IMPORT_DIGIGO" })}
                            type="primary"
                            className="px-3"
                            disabled={isProjectLoading}
                          >
                            Pending List From DigiGo
                          </Button>
                        </Link>
                      )}

                      {!!projectData?.data?.find(
                        (item) => item?.project?.id === 16
                      ) && (
                        <Link
                          to={`/admin/salary-disbursement/batch/pending-list/${
                            projectData?.data?.find(
                              (item) => item?.project?.id === 16
                            )?.project?.id
                          }`}
                        >
                          <Button
                            // onClick={() => handleOpen({ type: "IMPORT_DIGIGO" })}
                            type="primary"
                            className="px-3 !bg-[#1A1E51]"
                            disabled={isProjectLoading}
                          >
                            Pending List From Bondhu
                          </Button>
                        </Link>
                      )}
                    </>
                  )}

                  {walletRole === "maker" && roleCheck === "User" && (
                    <Button
                      onClick={() => handleOpen({ type: "IMPORT" })}
                      type="default"
                      className="!border !border-yellow-500 !text-yellow-500 !hover:border-yellow-500 text-sm font-medium"
                      icon={<PayrollImportOutlined />}
                    >
                      Create
                    </Button>
                  )}
                </>
              }
            />
          </>
        )}
      </div>
    </>
  );
}
